import React, { useState } from 'react';
import { Edit2, Trash2, ChevronDown, ChevronRight, Target, BookOpen, Users, Building2, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { Separator } from '../ui/separator';

interface FeatureCardProps {
  feature: any;
  onEdit: () => void;
  onDelete: () => void;
}

export function FeatureCard({ feature, onEdit, onDelete }: FeatureCardProps) {
  const [isOpen, setIsOpen] = useState(false);

  const getComplexityColor = (score: number) => {
    if (score <= 3) return 'bg-green-100 text-green-800';
    if (score <= 6) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getFrequencyColor = (frequency: string) => {
    switch (frequency) {
      case 'common': return 'bg-green-100 text-green-800';
      case 'uncommon': return 'bg-yellow-100 text-yellow-800';
      case 'rare': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getFormalityColor = (formality: string) => {
    switch (formality) {
      case 'formal': return 'bg-blue-100 text-blue-800';
      case 'informal': return 'bg-purple-100 text-purple-800';
      case 'neutral': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3 flex-1">
                <div className="flex items-center gap-2 mt-1">
                  {isOpen ? (
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  )}
                  <BookOpen className="w-4 h-4 text-purple-600" />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-base mb-2">{feature.grammar_feature}</CardTitle>
                  <div className="flex flex-wrap gap-2 mb-3">
                    <Badge variant="outline" className="text-xs">
                      {feature.feature_id}
                    </Badge>
                    {feature.complexity_score !== undefined && (
                      <Badge 
                        variant="secondary" 
                        className={getComplexityColor(feature.complexity_score)}
                      >
                        Complexity: {feature.complexity_score}
                      </Badge>
                    )}
                    {feature.cefr_introduction && (
                      <Badge variant="outline" className="text-xs">
                        {feature.cefr_introduction} → {feature.cefr_mastery || '?'}
                      </Badge>
                    )}
                    {feature.formality && (
                      <Badge 
                        variant="secondary" 
                        className={getFormalityColor(feature.formality)}
                      >
                        {feature.formality}
                      </Badge>
                    )}
                    {feature.frequency && (
                      <Badge 
                        variant="secondary" 
                        className={getFrequencyColor(feature.frequency)}
                      >
                        {feature.frequency}
                      </Badge>
                    )}
                  </div>
                  <CardDescription className="text-sm">
                    {feature.important_footnotes || feature.description}
                  </CardDescription>
                </div>
              </div>
              <div className="flex gap-1 ml-4">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                  }}
                >
                  <Edit2 className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <CardContent className="pt-0">
            <Separator className="mb-4" />
            
            {/* Examples Section */}
            {((feature.example_1 || feature.example_2) || 
              (feature.positive_examples && feature.positive_examples.length > 0)) && (
              <div className="mb-6">
                <h4 className="text-sm font-semibold flex items-center gap-2 mb-3">
                  <Target className="w-4 h-4 text-green-600" />
                  Examples
                </h4>
                <div className="space-y-2">
                  {feature.example_1 && (
                    <div className="bg-green-50 border-l-4 border-green-400 p-3 text-sm">
                      {feature.example_1}
                    </div>
                  )}
                  {feature.example_2 && (
                    <div className="bg-green-50 border-l-4 border-green-400 p-3 text-sm">
                      {feature.example_2}
                    </div>
                  )}
                  {feature.positive_examples?.map((example: string, idx: number) => (
                    <div key={idx} className="bg-green-50 border-l-4 border-green-400 p-3 text-sm">
                      {example}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Common Errors/Confusions Section */}
            {((feature.common_errors && feature.common_errors.length > 0) ||
              (feature.common_confusions && feature.common_confusions.length > 0)) && (
              <div className="mb-6">
                <h4 className="text-sm font-semibold flex items-center gap-2 mb-3">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                  Common Errors & Confusions
                </h4>
                <div className="space-y-2">
                  {feature.common_errors?.map((error: string, idx: number) => (
                    <div key={idx} className="bg-red-50 border-l-4 border-red-400 p-3 text-sm">
                      {error}
                    </div>
                  ))}
                  {feature.common_confusions?.map((confusion: any, idx: number) => (
                    <div key={idx} className="bg-orange-50 border-l-4 border-orange-400 p-3 text-sm">
                      <strong>Confused with {confusion.feature_id}:</strong> {confusion.explanation}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Business Contexts Section */}
            {feature.business_contexts && feature.business_contexts.length > 0 && (
              <div className="mb-6">
                <h4 className="text-sm font-semibold flex items-center gap-2 mb-3">
                  <Building2 className="w-4 h-4 text-blue-600" />
                  Business Contexts
                </h4>
                <div className="space-y-2">
                  {feature.business_contexts.map((context: any, idx: number) => (
                    <div key={idx} className="bg-blue-50 p-3 rounded-lg text-sm">
                      <div className="flex items-start justify-between">
                        <div>
                          <span className="font-medium capitalize">
                            {(context.context || context).replace(/_/g, ' ')}
                            {context.example && ':'}
                          </span>
                          {context.example && (
                            <p className="text-muted-foreground mt-1">{context.example}</p>
                          )}
                        </div>
                        {context.frequency && (
                          <Badge variant="outline" className="text-xs ml-2">
                            {context.frequency}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Related Features Section */}
            {((feature.related_features && feature.related_features.length > 0) ||
              (feature.prerequisites && feature.prerequisites.length > 0) ||
              (feature.enables && feature.enables.length > 0) ||
              (feature.combines_well_with && feature.combines_well_with.length > 0)) && (
              <div className="mb-6">
                <h4 className="text-sm font-semibold flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-purple-600" />
                  Related Features
                </h4>
                <div className="space-y-3">
                  {feature.prerequisites?.length > 0 && (
                    <div>
                      <span className="text-xs font-medium text-muted-foreground">Prerequisites:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {feature.prerequisites.map((prereq: string, idx: number) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {prereq}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {feature.enables?.length > 0 && (
                    <div>
                      <span className="text-xs font-medium text-muted-foreground">Enables:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {feature.enables.map((enabled: string, idx: number) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {enabled}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {feature.combines_well_with?.length > 0 && (
                    <div>
                      <span className="text-xs font-medium text-muted-foreground">Combines well with:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {feature.combines_well_with.map((combo: string, idx: number) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {combo}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {feature.related_features?.map((related: any, idx: number) => (
                    <Badge key={idx} variant="secondary" className="text-xs">
                      {related.feature_id || related}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            {/* Additional Details */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              {feature.structural_formula && (
                <div className="col-span-2">
                  <span className="text-muted-foreground">Structure:</span>
                  <code className="block mt-1 p-2 bg-gray-100 rounded text-xs">
                    {feature.structural_formula}
                  </code>
                </div>
              )}
              {feature.linguistic_category && (
                <div>
                  <span className="text-muted-foreground">Linguistic Category:</span>
                  <p className="font-medium">{feature.linguistic_category}</p>
                </div>
              )}
              {feature.functional_category && (
                <div>
                  <span className="text-muted-foreground">Functional Category:</span>
                  <p className="font-medium">{feature.functional_category}</p>
                </div>
              )}
              {feature.register && feature.register.length > 0 && (
                <div>
                  <span className="text-muted-foreground">Register:</span>
                  <div className="flex gap-1 mt-1">
                    {feature.register.map((reg: string, idx: number) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        {reg}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {feature.tags && feature.tags.length > 0 && (
                <div>
                  <span className="text-muted-foreground">Tags:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {feature.tags.map((tag: string, idx: number) => (
                      <span key={idx} className="text-xs px-2 py-0.5 bg-gray-100 rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}